/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000220 在庫照会
 */
import { Column, Query } from "@material-table/core";
import { B000190SokoNm, B000200ShohinNm, catchRestError } from "clients/rest";
import { B000220Find, B000220FindRequest } from "clients/rest/b000/B000220";
import {
  msgState,
  SCodeSelect,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
} from "components";
import {
  B000220Shutsuryokujun,
  getSCode,
  SCode,
  ShohinshubetsuKbn,
} from "messages/codes";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";
import { B000190 } from "./B000190";
import { B000200 } from "./B000200";
import { B000202 } from "./B000202";

// 呼び出し時のプロパティ
export type B000220Props = Partial<{
  shohinCd: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  shohinCd: string;
  shohinNm: string;
  shohinNmCriteria: string;
  shohinKana: string;
  shodaibun: string;
  shochubun: string;
  shoshobun: string;
  sokoCd: string;
  sokoNm: string;
  shohinshubetsuKbn: SCode;
  shutsuryokujun: SCode;
}>;

// テーブル定義
type Data = Partial<{
  gyoNo: string;
  shohinCd: string;
  shohinNm: string;
  sokoCd: string;
  sokoNm: string;
  zaikosu: string;
  hikiatekanosu: string;
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  shohinCd: "",
  shohinNm: "",
  shohinNmCriteria: "",
  shohinKana: "",
  shodaibun: "",
  shochubun: "",
  shoshobun: "",
  sokoCd: "",
  sokoNm: "",
  shohinshubetsuKbn: getSCode(B000220Shutsuryokujun, ""),
  shutsuryokujun: getSCode(B000220Shutsuryokujun, "0"),
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行", width: "2rem" },
  { field: "shohinCd", title: "商品コード", width: "7rem" },
  { field: "shohinNm", title: "商品名", width: "12rem" },
  { field: "sokoCd", title: "倉庫", width: "3rem" },
  { field: "sokoNm", title: "", width: "8rem" },
  {
    field: "zaikosu",
    title: "現在庫数",
    width: "5rem",
    align: "right",
    render: (data) => data.zaikosu?.toLocaleString(),
  },
  {
    field: "hikiatekanosu",
    title: "引当可能数",
    width: "5rem",
    align: "right",
    render: (data) => data.hikiatekanosu?.toLocaleString(),
  },
  { field: "janCd", hidden: true },
  { field: "sokoshubetsuKbn", hidden: true },
];

const focusId = "B000220";

export const B000220 = (props: B000220Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};
const Page = (props: B000220Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  //フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 商品コード処理
      if (props.shohinCd) {
        methods.setValue("shohinCd", props.shohinCd);
        await B000200ShohinNm(methods, navigate, setMsg);
        focus(2);
      }
      // 初回フォーカス先
      if (!props.shohinCd) {
        focus(1);
      }
    })();
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();

      // 出力順の値に応じたソート処理
      let order;
      if (fields.shutsuryokujun?.value === "0") {
        order = ["shohinCd,asc", "sokoCd,asc"];
      } else if (fields.shutsuryokujun?.value === "1") {
        order = ["sokoCd,asc", "shohinCd,asc"];
      } else if (fields.shutsuryokujun?.value === "2") {
        order = ["shohinNm,asc", "shohinCd,asc", "sokoCd,asc"];
      }

      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;

      // 検索パラメータを作成する。
      const param: B000220FindRequest = {
        shohinCd: fields.shohinCd,
        shohinNmCriteria: fields.shohinNmCriteria,
        shohinKana: fields.shohinKana,
        shodaibun: fields.shodaibun,
        shochubun: fields.shochubun,
        shoshobun: fields.shoshobun,
        sokoCd: fields.sokoCd,
        shohinshubetsuKbn: fields.shohinshubetsuKbn?.value,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: order,
      };
      // API呼び出し
      B000220Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame
        title="在庫照会"
        gridTemplateColumns="5rem 3rem 3rem 4rem 1rem 4rem 5rem 8rem 1rem 5rem 10rem"
      >
        <SLabel required>商品</SLabel>
        <SNumberField
          label="商品コード"
          name="shohinCd"
          required
          code
          size={10}
          {...register(1)}
          gridColumn="span 3"
          dialog={<B000200 />}
          handleSubmit={(data: any) => {
            methods.setValue("shohinCd", data.shohinCd);
            methods.setValue("shohinNm", data.shohinNm);
          }}
          onBlur={() => B000200ShohinNm(methods, navigate, setMsg)}
        />
        <STextField name="shohinNm" size={40} disabled gridColumn="span 3" />
        <SLabel gridColumn="1">商品名</SLabel>
        <STextField
          name="shohinNmCriteria"
          size={40}
          {...register(2)}
          gridColumn="span 6"
        />
        <SLabel gridColumn="1">カナ名</SLabel>
        <STextField
          name="shohinKana"
          size={40}
          {...register(3)}
          gridColumn="span 6"
        />
        <SLabel gridColumn="1">商品分類</SLabel>
        <SNumberField name="shodaibun" code size={3} {...register(4)} />
        <SNumberField name="shochubun" code size={3} {...register(5)} />
        <SNumberField
          name="shoshobun"
          code
          size={3}
          {...register(6)}
          dialog={<B000202 />}
          handleSubmit={(data: any) => {
            methods.setValue("shodaibun", data.shodaibun);
            methods.setValue("shochubun", data.shochubun);
            methods.setValue("shoshobun", data.shoshobun);
          }}
        />
        <SLabel gridColumn="6">倉庫</SLabel>
        <SNumberField
          label="倉庫コード"
          name="sokoCd"
          code
          size={3}
          {...register(7)}
          dialog={<B000190 />}
          handleSubmit={(data: any) => {
            methods.setValue("sokoCd", data?.sokoCd);
          }}
          onBlur={() => B000190SokoNm(methods, navigate, setMsg)}
        />
        <STextField name="sokoNm" size={20} disabled />
        <SLabel gridColumn="10">商品種別</SLabel>
        <SCodeSelect
          name="shohinshubetsuKbn"
          options={ShohinshubetsuKbn}
          {...register(8)}
        />
        <SLabel gridColumn="1">出力順</SLabel>
        <SCodeSelect
          name="shutsuryokujun"
          options={B000220Shutsuryokujun}
          {...register(9)}
          gridColumn="span 3"
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow={6}
          gridColumn={11}
          align="right"
          {...register(10)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
